import React from "react";
import { cn } from "utils";
import { Icon as OspIcon, ospThemes } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { EdgenModalCreateAble } from "../EdgenModal";
import { ChatShareModalProps, ShareOption, ShareResult } from "./types";
import Translations from "@translate/components/ChatShareModal/ChatShareModal";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

// ChatShareModal 内容组件
const ChatShareModalContent: React.FC<ChatShareModalProps> = ({
  className,
  onClose,
  shareOptions = [], // 添加默认值防止 undefined
  shareUrl,
  showCopyLink = true,
  onCopyLink,
  isAll,
}) => {
  const handleCopyLink = () => {
    onCopyLink?.();
  };

  return (
    <div
      className={cn("flex flex-col gap-5 items-start justify-start", className)}
      data-name="Chat Share Modal Content"
      data-node-id="44896:101549"
    >
      {/* Header */}
      <div className="flex flex-col gap-2 items-start justify-start w-full">
        <div className="flex items-center justify-between w-full">
          <h3
            aria-label="Chat Share Modal h3"
            data-openreplay-label="Chat Share Modal h3 Semantic"
            id="sem-chat-share-modal-h3"
            className="headline_regular_s text-content_primary"
          >
            {I18n.t("Share Chat")}
          </h3>
          {onClose && (
            <button
              data-openreplay-label="Close Button"
              id="btn-close-chatsharemodal-chatsharemodal"
              onClick={onClose}
              className={cn(
                "rounded-full w-7 h-7",
                "flex items-center justify-center",
                "absolute top-3 right-3",
              )}
              aria-label="Close"
            >
              <OspIcon
                name="close"
                size={16}
                color={ospThemes.colors.content_primary}
                className="hover:text-content_primary transition-colors"
              />
            </button>
          )}
        </div>
        <p className="body_m text-content_secondary">
          {isAll
            ? I18n.t("Anyone with this link can view the conversation")
            : I18n.t(
                "The share page will include messages up until this point",
              )}
        </p>
      </div>
      {/* Dynamic Share Options */}
      {shareOptions && shareOptions.length > 0 && (
        <div className="flex gap-6 items-center justify-center w-full">
          {shareOptions.map((option) => (
            <div
              key={option.id}
              className={cn(
                "flex flex-col gap-1 grow items-center justify-start",
                {
                  ["px-6 flex-grow-0"]: shareOptions.length < 3,
                },
              )}
            >
              <button
                data-openreplay-label="Chat Share Modal Button"
                id="btn-chat-share-modal"
                onClick={option.onClick}
                className={cn(
                  "bg-background_white_overlay10 relative rounded-full w-14 h-14",
                  "flex items-center justify-center",
                  "hover:bg-background_overlay15 transition-all duration-200",
                )}
                aria-label={option.title}
              >
                <OspIcon
                  name={option.icon}
                  size={24}
                  color={ospThemes.colors.content_primary}
                />
              </button>
              <span className="body_s text-content_tertiary text-center">
                {option.title}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Conditional Divider */}
      {shareOptions && shareOptions.length > 0 && showCopyLink && (
        <div className="flex gap-4 items-center justify-start w-full">
          <div className="basis-0 bg-background_white_overlay10 grow h-px" />
          <span className="body_m text-content_tertiary text-center">
            {I18n.t("or")}
          </span>
          <div className="basis-0 bg-background_white_overlay10 grow h-px" />
        </div>
      )}
      {/* Conditional Copy Link Section */}
      {showCopyLink && (
        <div className="bg-background_solid_tertiary box-border flex items-center justify-between pl-3 pr-2 py-3 rounded-2 w-full flex">
          <div className="body_m text-content_tertiary overflow-hidden overflow-hidden whitespace-nowrap relative">
            {shareUrl}
            <div
              className="absolute top-0 right-0 h-full w-5"
              style={{
                background:
                  "linear-gradient(270deg, var(--edgen-background_solid_tertiary) 12.02%, rgba(37, 37, 37, 0.00) 100%)",
              }}
            />
          </div>
          <button
            data-openreplay-label="Copy Link Button"
            id="btn-copy-link"
            onClick={handleCopyLink}
            className={cn(
              "primary_button xs_button",
              "bg-content_primary text-content_inverse_primary",
              "flex items-center gap-1",
            )}
            aria-label="Copy Link"
          >
            <OspIcon
              name="copy-action"
              size={16}
              color={ospThemes.colors.content_inverse_primary}
            />
            <span className="button_s">{I18n.t("Copy Link")}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ChatShareModal 容器组件（用于直接使用）
export const ChatShareModal: React.FC<ChatShareModalProps> = (props) => {
  return (
    <div className="p-6 max-md-py-4 max-md-px-4 bg-background_solid_secondary relative">
      <ChatShareModalContent {...props} />
    </div>
  );
};

// ChatShareModal 可调用版本（推荐使用）
export const ChatShareModalCreateAble = async (props: {
  shareOptions: ShareOption[];
  shareUrl?: string;
  showCopyLink?: boolean;
  onCopyLink?: () => void;
  isAll: boolean;
}): Promise<ShareResult> => {
  return new Promise((resolve) => {
    // 防御性检查，确保 shareOptions 存在
    if (!props.shareOptions || !Array.isArray(props.shareOptions)) {
      log.error(
        "[ChatShareModalCreateAble] shareOptions is required and must be an array",
      );
      resolve({ action: "close" });
      return;
    }

    // 包装分享选项的 onClick，使其能返回结果
    const wrappedShareOptions = props.shareOptions.map((option) => ({
      ...option,
      onClick: async () => {
        await EdgenModalCreateAble.end(false);
        option.onClick();
        resolve({ action: "share", optionId: option.id });
      },
    }));

    EdgenModalCreateAble.call({
      children: (
        <ChatShareModal
          isAll={props.isAll}
          shareOptions={wrappedShareOptions}
          shareUrl={props.shareUrl}
          showCopyLink={props.showCopyLink}
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve({ action: "close" });
          }}
          onCopyLink={() => {
            props.onCopyLink?.();
            EdgenModalCreateAble.end(false);
            resolve({ action: "copy" });
          }}
        />
      ),
      containerClassName: "!md:w-[400px] !md:rounded-2xl",
      styles: {
        content: {
          padding: 0,
          borderRadius: "16px",
        },
      },
      destroyOnClose: true,
    });
  });
};

export default ChatShareModal;
